<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, AlertCircle, AlertTriangle, Info, X, Loader2 } from 'lucide-vue-next'
import { useToast, type Toast, type ToastAction, type ToastStatus } from '../composables/useToast'

// The app's one toast design (see useToast.ts for the API). Bottom-right,
// bottom-centre under 640px, newest at the bottom, at most three on screen.
const { toasts, toast, remove, pause, resume } = useToast()
const dark = computed(() => toasts.value.length > 0 && document.documentElement.classList.contains('dark'))
const icon: Record<ToastStatus, unknown> = { success: Check, error: AlertCircle, warning: AlertTriangle, info: Info }
const color: Record<ToastStatus, string> = { success: '#12805C', error: '#B42318', warning: '#B54708', info: '#1F6FEB' }
const role = (t: Toast) => t.type === 'error' || t.type === 'warning' ? 'alert' : 'status'
// The toast stays (timer held, action spinning, re-clicks ignored) until its action settles. A failure the
// action already reported (marked `toasted`, e.g. the Sweep deal store) is not toasted again.
const running = ref<{ id: number; label: string } | null>(null)
async function act(t: Toast, a: ToastAction) {
    if (running.value) return
    running.value = { id: t.id, label: a.label }; pause(t.id)
    try { await a.onClick?.() }
    catch (e) { if (!(e as { toasted?: boolean })?.toasted) toast({ status: 'error', title: 'That did not work', description: (e as Error)?.message }) }
    finally { running.value = null; remove(t.id) }
}
</script>

<template>
    <Teleport to="body">
        <div class="fixed z-[9999] inset-x-4 bottom-6 flex flex-col items-center gap-3 pointer-events-none sm:inset-x-auto sm:right-6 sm:items-end">
            <TransitionGroup name="cc-toast">
                <div
                    v-for="t in toasts"
                    :key="t.id"
                    class="cc-toast pointer-events-auto w-[420px] max-w-[calc(100vw-32px)] bg-cc-elevated border border-cc-border-med rounded-[14px] overflow-hidden"
                    :style="{ '--toast-color': color[t.type], '--toast-ms': `${t.duration}ms` }"
                    :role="role(t)"
                    :aria-live="role(t) === 'status' ? 'polite' : undefined"
                    data-testid="toast"
                    :data-kind="t.type"
                    @mouseenter="pause(t.id)"
                    @mouseleave="resume(t.id)"
                    @focusin="pause(t.id)"
                    @focusout="resume(t.id)"
                    @keydown.esc="remove(t.id)"
                >
                    <div class="flex items-center pl-[18px] pr-1.5 pt-1.5">
                        <!-- The four-dot mark is the left end of the wordmark; crop it rather than ship another asset. -->
                        <img :src="dark ? '/assets/companion-on-dark.svg' : '/assets/companion-on-light.svg'" alt="" class="w-4 h-4 object-cover object-left" />
                        <span class="ml-2 text-[13px] font-semibold text-cc-text-secondary">Companion</span>
                        <span class="ml-1.5 text-[13px] text-cc-text-tertiary">· just now</span>
                        <button type="button" class="ml-auto w-9 h-9 flex items-center justify-center rounded-lg text-cc-text-tertiary hover:text-cc-text hover:bg-cc-hover transition-colors" aria-label="Dismiss notification" @click="remove(t.id)"><X class="w-4 h-4" /></button>
                    </div>
                    <div class="flex gap-3.5 px-[18px] pt-1 pb-4">
                        <span class="cc-toast-icon w-9 h-9 rounded-full flex-none flex items-center justify-center" aria-hidden="true"><component :is="icon[t.type]" class="w-5 h-5" /></span>
                        <div class="min-w-0 flex-1 break-words">
                            <div class="text-[15px] font-semibold leading-[22px] text-cc-text">{{ t.message }}</div>
                            <div v-if="t.description" class="text-[14px] leading-5 text-cc-text-secondary">{{ t.description }}</div>
                            <div v-if="t.actions?.length" class="mt-2 flex gap-5">
                                <template v-for="(a, i) in t.actions" :key="a.label">
                                    <RouterLink v-if="a.href" :to="a.href" :class="['text-[14px] font-semibold', i ? 'text-cc-text-secondary' : 'cc-toast-primary']" data-testid="toast-action" @click="act(t, a)">{{ a.label }}</RouterLink>
                                    <button v-else type="button" :class="['inline-flex items-center gap-1.5 text-[14px] font-semibold disabled:opacity-60', i ? 'text-cc-text-secondary' : 'cc-toast-primary']" :disabled="!!running" data-testid="toast-action" @click="act(t, a)"><Loader2 v-if="running?.id === t.id && running.label === a.label" class="w-3.5 h-3.5 animate-spin" />{{ a.label }}</button>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div v-if="t.duration > 0" class="h-[3px] bg-cc-hover" aria-hidden="true"><div class="cc-toast-bar h-full" /></div>
                </div>
            </TransitionGroup>
        </div>
    </Teleport>
</template>

<style scoped>
.cc-toast { box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 12px 32px rgba(16, 24, 40, 0.12); }
.cc-toast-icon { background: color-mix(in srgb, var(--toast-color) 12%, transparent); color: var(--toast-color); }
.cc-toast-primary { color: var(--toast-color); }
.cc-toast-bar { width: 100%; background: var(--toast-color); animation: cc-toast-drain var(--toast-ms) linear forwards; }
.cc-toast:hover .cc-toast-bar, .cc-toast:focus-within .cc-toast-bar { animation-play-state: paused; }
@keyframes cc-toast-drain { to { width: 0; } }
.cc-toast-enter-active { transition: opacity 200ms ease, transform 200ms ease; }
.cc-toast-leave-active { transition: opacity 150ms ease; }
.cc-toast-enter-from { opacity: 0; transform: translateY(8px); }
.cc-toast-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
    .cc-toast-enter-active, .cc-toast-leave-active { transition: none; }
    .cc-toast-enter-from { transform: none; }
}
</style>
